// Single-prompt onboarding. The customer describes the website they want;
// we generate the full site in the background and show a phase-by-phase
// progress bar while we work. No more 10-step questionnaire.
//
// Auth & business-cap checks happen server-side in /api/business/quickstart.
// On success we navigate straight to the dashboard, which loads the new biz.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Loader2, Check, AlertCircle, Wand2 } from "lucide-react";
import { getStoredPlanSelection, clearPlanSelection, INTERVAL_LABELS } from "@/lib/planSelection";

const POLL_MS = 1000;
const PHASE_ORDER = [
  "queued", "analyzing", "planning", "writing",
  "designing", "imaging", "building", "finalizing", "done",
];

const PROMPT_EXAMPLES = [
  "Create a website for a small business consulting company.",
  "Create a website for a restaurant in Brooklyn.",
  "Create a website for a clothing brand.",
  "Create an RSVP website for a luxury event.",
  "Create a website for a wedding photographer in Richmond, Virginia.",
  "Create a B2B website for a marketing consultant.",
];


function Sparkle({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M12 2l1.6 6.4L20 10l-6.4 1.6L12 18l-1.6-6.4L4 10l6.4-1.6L12 2z" />
    </svg>
  );
}


export default function Onboarding() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [prompt, setPrompt] = useState(() => {
    if (typeof window === "undefined") return "";
    // "iti.pendingPrompt" wins over "iti.initialBusinessIdea": it means the
    // user just returned from a paid-plan Stripe checkout with a prompt they
    // saved before paying. We *don't* remove it here — the paid-return
    // effect below handles the removal after successfully firing quickstart.
    const pending = localStorage.getItem("iti.pendingPrompt");
    if (pending) return pending;
    const seed = localStorage.getItem("iti.initialBusinessIdea") || "";
    if (seed) localStorage.removeItem("iti.initialBusinessIdea");
    return seed;
  });
  const [agreed, setAgreed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [phases, setPhases] = useState([]);
  const [job, setJob] = useState(null);  // { job_id, phase, percent, label, business_id, error }
  const [payingPlan, setPayingPlan] = useState(false); // Stripe-checkout redirect in progress
  const [awaitingPayment, setAwaitingPayment] = useState(false); // returned from Stripe, polling status
  const pollRef = useRef(null);

  // Selected paid plan × billing interval (if any). Cleared only via the
  // "Use free plan instead" escape OR after the paid flow completes.
  const planSelection = getStoredPlanSelection();
  const selectedPaidPlanId = planSelection?.planId || null;
  const selectedInterval = planSelection?.interval || "monthly";

  // Static phase list (so we can pre-render even before the first poll lands)
  useEffect(() => {
    api.get("/business/quickstart/phases")
      .then(({ data }) => setPhases(data.phases || []))
      .catch(() => { /* non-fatal — we'll just show a generic bar */ });
  }, []);

  // Send unauth'd users to log in
  useEffect(() => {
    if (!authLoading && !user) navigate("/", { replace: true });
  }, [user, authLoading, navigate]);

  // Polling: tick while a job is open
  useEffect(() => {
    if (!job?.job_id || job.phase === "done" || job.phase === "failed") return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/business/quickstart/status/${job.job_id}`);
        setJob(data);
        if (data.phase === "done" && data.business_id) {
          // Dashboard reads activeBizId from localStorage to pick the new business
          localStorage.setItem("iti.activeBizId", data.business_id);
          setTimeout(() => navigate("/dashboard", { replace: true }), 600);
        }
      } catch (e) {
        // Don't blow away the job on transient errors — just keep polling.
      }
    };
    pollRef.current = setInterval(tick, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [job?.job_id, job?.phase, navigate]);

  // Acknowledge legal acceptance, then either (a) kick off free generation
  // immediately, or (b) redirect to Stripe checkout so the paid plan is
  // charged BEFORE we spend generation credits.
  const submit = async () => {
    const text = prompt.trim();
    if (text.length < 5) { toast.error("Tell us a bit more about your website."); return; }
    if (!agreed) { toast.error("Please accept the Terms & Privacy Policy to continue."); return; }

    // Log legal acceptance up-front for both flows.
    try { await api.post("/legal/accept", { accepted: true, source: "onboarding" }); }
    catch { /* non-blocking */ }

    if (selectedPaidPlanId) {
      // Pay-before-generate flow — real Stripe SUBSCRIPTION checkout for
      // (plan × interval). Stash the prompt so we can auto-run quickstart
      // after Stripe returns the user to /onboarding?paid=1.
      setPayingPlan(true);
      try {
        localStorage.setItem("iti.pendingPrompt", text);
        localStorage.setItem("iti.pendingPlanId", selectedPaidPlanId);
        localStorage.setItem("iti.pendingInterval", selectedInterval);
        const { data } = await api.post("/billing/subscription/checkout", {
          plan_id: selectedPaidPlanId,
          interval: selectedInterval,
          origin_url: window.location.origin,
          success_path: "/onboarding?paid=1&session_id={CHECKOUT_SESSION_ID}",
          cancel_path: "/onboarding?paid=cancel",
        });
        if (!data?.url) throw new Error("No checkout URL returned");
        window.location.href = data.url;
      } catch (e) {
        if (e?.response?.status === 409) {
          // Already on an active subscription — no new payment needed.
          toast.info("You already have an active subscription — generating without a new payment.");
          clearPlanSelection();
          localStorage.removeItem("iti.pendingPrompt");
          setPayingPlan(false);
          setCreating(true);
          try {
            const { data: qs } = await api.post("/business/quickstart", { prompt: text });
            setJob({ job_id: qs.job_id, phase: "queued", percent: 0, label: "Queued" });
          } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't start generation");
            setCreating(false);
          }
          return;
        }
        toast.error(e.response?.data?.detail || "Couldn't start checkout");
        setPayingPlan(false);
      }
      return;
    }

    // Free plan: generate immediately.
    setCreating(true);
    try {
      const { data } = await api.post("/business/quickstart", { prompt: text });
      setJob({ job_id: data.job_id, phase: "queued", percent: 0, label: "Queued" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start generation");
      setCreating(false);
    }
  };

  // Handle Stripe return: verify the plan was paid, then auto-fire the
  // quickstart with the stashed prompt (no re-typing needed).
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") === "cancel") {
      toast.info("Payment was cancelled — your prompt was saved. Try again when you're ready.");
      window.history.replaceState({}, "", "/onboarding");
      return;
    }
    const sessionId = params.get("session_id");
    if (params.get("paid") !== "1" || !sessionId) return;

    setAwaitingPayment(true);
    let attempt = 0;
    const verifyAndGenerate = async () => {
      try {
        const { data } = await api.get(`/billing/subscription/checkout/${sessionId}`);
        if (data.payment_status === "paid") {
          const savedPrompt = localStorage.getItem("iti.pendingPrompt") || "";
          if (!savedPrompt) {
            toast.error("Couldn't recover your prompt — please enter it again.");
            setAwaitingPayment(false);
            clearPlanSelection();
            window.history.replaceState({}, "", "/onboarding");
            return;
          }
          // Legal accept was captured before Stripe redirect; auto-consent
          // by setting the checkbox in state so `submit()` invariants hold.
          toast.success("Payment received — building your website now.");
          setCreating(true);
          setAwaitingPayment(false);
          window.history.replaceState({}, "", "/onboarding");
          try {
            const { data: qs } = await api.post("/business/quickstart", { prompt: savedPrompt });
            setJob({ job_id: qs.job_id, phase: "queued", percent: 0, label: "Queued" });
            // Clear all stashed selection now that generation is under way.
            localStorage.removeItem("iti.pendingPrompt");
            clearPlanSelection();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't start generation");
            setCreating(false);
          }
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout session expired. Please try again.");
          setAwaitingPayment(false);
          window.history.replaceState({}, "", "/onboarding");
          return;
        }
        if (attempt < 10) { attempt += 1; setTimeout(verifyAndGenerate, 2000); }
        else {
          toast.error("Payment is taking longer than expected. Try refreshing.");
          setAwaitingPayment(false);
        }
      } catch (e) {
        if (attempt < 10) { attempt += 1; setTimeout(verifyAndGenerate, 2000); }
        else setAwaitingPayment(false);
      }
    };
    verifyAndGenerate();
  }, [user]);

  // ---------- Render ----------
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center iti-bg-soft">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Awaiting-payment screen (returned from Stripe, verifying + about to generate)
  if (awaitingPayment) {
    return (
      <div className="min-h-screen iti-bg-soft flex items-center justify-center" data-testid="awaiting-payment">
        <div className="iti-card p-10 text-center max-w-md">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-600 mx-auto mb-4" />
          <div className="font-display text-2xl font-extrabold text-slate-900">Confirming your payment…</div>
          <p className="text-sm text-slate-600 mt-2">
            Stripe is confirming your purchase. We&apos;ll start generating your
            website the moment it clears — usually a few seconds.
          </p>
        </div>
      </div>
    );
  }

  // Job in progress (or finished / failed): show progress UI
  if (job) return <Progress job={job} phases={phases} onRetry={() => { setJob(null); setCreating(false); }} />;

  return (
    <div className="min-h-screen iti-bg-soft">
      <nav className="sticky top-0 z-40 backdrop-blur-md bg-white/70 border-b border-emerald-100/70">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="onboarding-logo">
            <span className="iti-sparkle"><Sparkle className="w-5 h-5" /></span>
            <span className="font-display text-[17px] font-extrabold tracking-tight text-slate-900">
              Interest to Income
            </span>
          </div>
          <div className="text-xs text-slate-500 hidden sm:block">
            Logged in as <span className="font-semibold text-slate-700">{user?.email}</span>
          </div>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16">
        <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] text-emerald-700 uppercase">
          <Sparkles className="w-3 h-3" /> New website
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-3 text-slate-900 leading-[1.05]">
          Describe the website you <span className="font-script text-emerald-600">want</span>
          <span className="text-emerald-600">.</span>
        </h1>
        <p className="text-base sm:text-lg text-slate-600 mt-4 max-w-xl">
          Tell us in one sentence — we&apos;ll do the rest. No 10-step questionnaire.
        </p>

        {selectedPaidPlanId && (
          <div
            className="mt-6 iti-card p-4 flex items-center gap-3 bg-emerald-50/60 border-emerald-200/80"
            data-testid="selected-plan-banner"
          >
            <div className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4" strokeWidth={3} />
            </div>
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-semibold text-slate-800" data-testid="selected-plan-label">
                You picked the <b className="capitalize">{selectedPaidPlanId}</b> plan —{" "}
                <b>{INTERVAL_LABELS[selectedInterval]}</b> billing.
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                Write your prompt below, then we&apos;ll take payment first and start building the moment it clears.
              </div>
            </div>
            <button
              onClick={() => {
                clearPlanSelection();
                window.location.reload();
              }}
              className="text-xs font-semibold text-emerald-700 underline hover:no-underline flex-shrink-0"
              data-testid="clear-plan-btn"
            >
              Use free plan instead
            </button>
          </div>
        )}

        <div className="mt-8 iti-card p-6 sm:p-8">
          <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
            Your website prompt
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            data-testid="quickstart-prompt"
            placeholder="Create a website for a wedding photographer in Richmond, Virginia."
            rows={4}
            className="w-full p-4 rounded-2xl border border-emerald-200/80 outline-none text-[15px] text-slate-800 placeholder:text-slate-400 focus:border-emerald-500 focus:shadow-[0_0_0_4px_rgba(16,185,129,0.12)] transition"
            disabled={creating}
          />

          <div className="mt-3 flex flex-wrap gap-2">
            {PROMPT_EXAMPLES.map((ex, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPrompt(ex)}
                disabled={creating}
                data-testid={`example-${i}`}
                className="iti-chip text-xs font-semibold px-3 py-1.5"
              >
                {ex}
              </button>
            ))}
          </div>

          <label className="mt-6 flex items-start gap-3 p-4 rounded-2xl border border-emerald-100 bg-emerald-50/40 cursor-pointer"
                 data-testid="legal-accept-row">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="legal-accept-checkbox"
              disabled={creating}
              className="mt-1 w-5 h-5 accent-emerald-600 shrink-0 cursor-pointer"
            />
            <span className="text-sm text-slate-700">
              I agree to the{" "}
              <a href="/terms" target="_blank" rel="noreferrer" className="text-emerald-700 underline font-semibold">Terms &amp; Conditions</a>{" "}
              and{" "}
              <a href="/privacy" target="_blank" rel="noreferrer" className="text-emerald-700 underline font-semibold">Privacy Policy</a>.
              <span className="block text-xs text-slate-500 mt-1">
                Interest To Income provides tools and guides — not a guarantee of business success.
                You are responsible for your own research, execution, and outcomes.
              </span>
            </span>
          </label>

          <button
            onClick={submit}
            disabled={creating || payingPlan || !prompt.trim() || !agreed}
            data-testid="quickstart-submit"
            className="iti-btn-primary mt-6 px-6 py-3.5 text-base font-semibold inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {(creating || payingPlan) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {payingPlan
              ? "Redirecting to Stripe…"
              : creating
                ? "Starting…"
                : selectedPaidPlanId
                  ? `Pay & generate — ${selectedPaidPlanId} (${INTERVAL_LABELS[selectedInterval]})`
                  : "Generate my website"}
            <ArrowRight className="w-4 h-4" />
          </button>

          <p className="mt-4 text-xs text-slate-500">
            Mention a <b>location</b> for local SEO (e.g. &ldquo;in Richmond, Virginia&rdquo;).
            Add <b>B2B</b> if your customers are businesses.
          </p>
        </div>
      </main>
    </div>
  );
}


/* ------------------------ Progress UI ------------------------ */

function Progress({ job, phases, onRetry }) {
  const isDone = job.phase === "done";
  const isFailed = job.phase === "failed";
  const currentIdx = PHASE_ORDER.indexOf(job.phase);
  const list = phases.length ? phases : PHASE_ORDER.slice(0, -1).map((id) => ({ id, label: id }));

  return (
    <div className="min-h-screen iti-bg-soft" data-testid="quickstart-progress">
      <main className="max-w-2xl mx-auto px-6 md:px-10 py-16">
        <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] text-emerald-700 uppercase">
          <Sparkles className="w-3 h-3" /> Building your website
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-3 text-slate-900 leading-tight">
          {isDone ? (
            <>Your website is <span className="font-script text-emerald-600">ready</span><span className="text-emerald-600">.</span></>
          ) : isFailed ? (
            <>Something went <span className="font-script text-red-600">wrong</span><span className="text-red-600">.</span></>
          ) : (
            <>Hold tight — we&apos;re <span className="font-script text-emerald-600">building</span><span className="text-emerald-600">.</span></>
          )}
        </h1>

        <div className="mt-8 iti-card p-6 sm:p-8">
          {/* Big progress bar */}
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-slate-700" data-testid="progress-label">
              {job.label || "Working…"}
            </div>
            <div className="text-sm font-bold text-emerald-700" data-testid="progress-percent">
              {Math.max(5, job.percent || 0)}%
            </div>
          </div>
          <div className="h-3 rounded-full bg-emerald-50 border border-emerald-100 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-700"
              style={{ width: `${Math.max(5, job.percent || 0)}%` }}
            />
          </div>

          {/* Phase list */}
          <ul className="mt-6 space-y-2" data-testid="phase-list">
            {list.filter((p) => p.id !== "queued").map((p) => {
              const idx = PHASE_ORDER.indexOf(p.id);
              const done = isDone || idx < currentIdx;
              const active = !isDone && !isFailed && idx === currentIdx;
              return (
                <li key={p.id} className="flex items-center gap-3 text-sm" data-testid={`phase-${p.id}`}>
                  <div className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0
                    ${done ? "bg-emerald-500 border-emerald-500 text-white" : active ? "bg-white border-emerald-500 text-emerald-600" : "bg-white border-slate-200 text-slate-300"}`}>
                    {done ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> :
                     active ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                     <span className="w-1.5 h-1.5 rounded-full bg-current" />}
                  </div>
                  <span className={done ? "text-slate-700" : active ? "font-semibold text-slate-900" : "text-slate-400"}>
                    {p.label}
                  </span>
                </li>
              );
            })}
          </ul>

          {isFailed && (
            <div className="mt-6 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2 text-sm text-red-700" data-testid="quickstart-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <b>We hit a snag.</b> {job.error || "Please try again with a slightly different prompt."}
              </div>
            </div>
          )}

          {isFailed && (
            <button onClick={onRetry} className="iti-btn-primary mt-6 px-5 py-2.5 text-sm font-semibold" data-testid="quickstart-retry">
              Try again
            </button>
          )}

          {!isDone && !isFailed && (
            <p className="mt-5 text-xs text-slate-500">
              This usually takes 60–120 seconds. We&apos;re writing your copy and generating images right now.
            </p>
          )}

          {isDone && (
            <p className="mt-5 text-sm text-slate-600">
              Taking you to your dashboard…
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
